import React from "react";
import { Box, Grid } from "@mui/material";
import { useApp, useTheme } from "contexts";
import { Custom } from "components";
import { EnumTheme } from "utils/enums";

type Props = {};

export const Overview = (props: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();
  const isDark = theme.id === EnumTheme.Dark;

  return (
    <Box
      component="section"
      id="overview"
      sx={{ pt: { xs: theme.spacing.xl, md: theme.spacing.xxl }, pb: theme.spacing.sm, width: "100%", position: "relative" }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "flex-end" },
          justifyContent: "space-between",
          mb: theme.spacing.xl,
          gap: theme.spacing.sm,
        }}
      >
        <Box>
          <Box
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxs,
              fontWeight: theme.font.bold,
              color: theme.color.primary.color,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            {t.sections.overview_badge}
          </Box>
          <Custom.Typography
            variant="h2"
            sx={{
              fontSize: { xs: theme.font.xl, md: theme.font.xxl },
              fontWeight: theme.font.extraBold,
              letterSpacing: "-0.02em",
              mt: theme.spacing.xxs,
            }}
          >
            {t.title.overview}
          </Custom.Typography>
        </Box>
        <Custom.Typography
          variant="body2"
          sx={{
            color: theme.color.font.accent,
            maxWidth: 420,
            fontSize: theme.font.xs,
            lineHeight: 1.6,
          }}
        >
          {t.sections.overview_summary}
        </Custom.Typography>
      </Box>

      {/* Grid of Competencies - exactly 3 per row on desktop (md={4}) */}
      <Grid container spacing={theme.spacing.md}>
        {t.overview.list.map((item: any, i: number) => {
          const indexFormatted = String(i + 1).padStart(2, "0");
          const isFeatured = i === 1 || i === 2;

          return (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Box
                sx={{
                  height: "100%",
                  p: theme.spacing.lg,
                  borderRadius: theme.border.square,
                  backgroundColor: isFeatured
                    ? theme.color.theme.color
                    : theme.color.background.accent,
                  border: `1px solid ${
                    isFeatured
                      ? theme.color.primary.color
                      : theme.color.border.color
                  }`,
                  boxShadow: `2px 2px 0px 0px ${
                    isFeatured
                      ? theme.color.primary.color
                      : theme.color.border.color
                  }`,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  transition:
                    "transform 0.15s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.15s cubic-bezier(0.2, 0, 0, 1)",
                  "&:hover": {
                    transform: "translate(-2px, -2px)",
                    boxShadow: `5px 5px 0px 0px ${
                      isDark ? "#000000" : "#191b22"
                    }`,
                    borderColor: isDark ? "#ffffff" : "#191b22",
                  },
                }}
              >
                {/* Micro Header with Index badge */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: theme.spacing.md,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: theme.font.xxs,
                      fontWeight: theme.font.bold,
                      px: theme.spacing.xs,
                      py: theme.spacing.xxs,
                      backgroundColor: isFeatured
                        ? theme.color.primary.color
                        : theme.color.theme.color,
                      color: isFeatured
                        ? theme.color.primary.text
                        : theme.color.font.accent,
                      border: `1px solid ${theme.color.border.color}`,
                      borderRadius: theme.border.square,
                    }}
                  >
                    {t.sections.module_prefix}{indexFormatted}
                  </Box>
                  {isFeatured && (
                    <Box
                      sx={{
                        fontFamily: '"Space Mono", monospace',
                        fontSize: theme.font.xxxs,
                        fontWeight: theme.font.bold,
                        color: theme.color.primary.color,
                        letterSpacing: "0.06em",
                      }}
                    >
                      {t.sections.featured_trait}
                    </Box>
                  )}
                </Box>

                {/* Title */}
                <Custom.Typography
                  variant="h3"
                  weight={theme.font.bold}
                  sx={{
                    fontSize: { xs: theme.font.sm, md: theme.font.md },
                    letterSpacing: "-0.01em",
                    mb: theme.spacing.sm,
                    color: theme.color.font.color,
                  }}
                >
                  {item.title}
                </Custom.Typography>

                {/* Description */}
                <Custom.Typography
                  variant="body2"
                  sx={{
                    color: theme.color.font.accent,
                    lineHeight: 1.65,
                    fontSize: theme.font.xs,
                    flexGrow: 1,
                  }}
                >
                  {item.short_description}
                </Custom.Typography>

                {/* Tactile Accent Footer */}
                <Box
                  sx={{
                    pt: theme.spacing.sm,
                    mt: theme.spacing.md,
                    borderTop: `1px dashed ${theme.color.border.color}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      backgroundColor: isFeatured
                        ? theme.color.primary.color
                        : theme.color.border.color,
                    }}
                  />
                  <Box
                    sx={{
                      fontFamily: '"Space Mono", monospace',
                      fontSize: theme.font.xxxs,
                      color: theme.color.font.accent,
                    }}
                  >
                    {t.sections.status_ready}
                  </Box>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};